import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { 
  getTicketById, 
  addMessage, 
  updateTicketStatus,
  Ticket, 
  TicketMessage,
  formatFileSize,
  getFileIcon 
} from '../services/ticketService';
import API_CONFIG from '../config/api';
import { toast } from 'react-toastify';

const TicketDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [newAttachments, setNewAttachments] = useState<FileList | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Admin state
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [resolution, setResolution] = useState('');

  // Check if user is admin
  const userRole = localStorage.getItem('userRole');
  const isAdmin = userRole === 'admin';

  // Status options
  const statusOptions = [
    { value: 'open', label: 'باز' },
    { value: 'in_progress', label: 'در حال انجام' },
    { value: 'waiting_user', label: 'در انتظار پاسخ کاربر' },
    { value: 'waiting_admin', label: 'در انتظار پاسخ ادمین' },
    { value: 'resolved', label: 'حل شده' },
    { value: 'closed', label: 'بسته شده' }
  ];

  // Fetch ticket details
  const fetchTicket = async () => {
    if (!id) {
      toast.error('شناسه تیکت نامعتبر است');
      navigate('/tickets');
      return;
    }

    try {
      setLoading(true);
      const response = await getTicketById(id);
      
      if (response.success && response.data.ticket) {
        setTicket(response.data.ticket);
        setNewStatus(response.data.ticket.status);
      }
    } catch (error) {

      toast.error('خطا در دریافت اطلاعات تیکت');
      navigate('/tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTicket();
  }, [id]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [ticket?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      // Validate file size (max 10MB per file)
      const maxSize = 10 * 1024 * 1024; // 10MB
      let hasOversizedFile = false;
      
      for (let i = 0; i < files.length; i++) {
        if (files[i].size > maxSize) {
          hasOversizedFile = true;
          break;
        }
      }
      
      if (hasOversizedFile) {
        toast.error('حداکثر حجم هر فایل باید 10 مگابایت باشد');
        return;
      }
      
      if (files.length > 5) {
        toast.error('حداکثر 5 فایل می‌توانید آپلود کنید');
        return;
      }
      
      setNewAttachments(files);
    }
  };

  // Remove attachment
  const removeAttachment = (index: number) => {
    if (newAttachments) {
      const dt = new DataTransfer();
      const files = Array.from(newAttachments);
      
      files.forEach((file, i) => {
        if (i !== index) {
          dt.items.add(file);
        }
      });
      
      setNewAttachments(dt.files);
      if (fileInputRef.current) {
        fileInputRef.current.files = dt.files;
      }
    }
  };

  // Send message
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newMessage.trim() && (!newAttachments || newAttachments.length === 0)) {
      toast.error('لطفاً پیام یا فایل ارسال کنید');
      return;
    }

    if (!ticket) return;

    try {
      setSendingMessage(true);
      
      await addMessage(ticket._id, newMessage.trim(), newAttachments || undefined);
      
      // Clear form
      setNewMessage('');
      setNewAttachments(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Refresh ticket data
      await fetchTicket();
      
      toast.success('پیام با موفقیت ارسال شد');
      
    } catch (error) {

      toast.error('خطا در ارسال پیام');
    } finally {
      setSendingMessage(false);
    }
  };

  // Update ticket status (admin only)
  const handleStatusUpdate = async () => {
    if (!ticket || !isAdmin) return;

    try {
      setUpdatingStatus(true);
      
      await updateTicketStatus(
        ticket._id, 
        newStatus, 
        undefined, 
        newStatus === 'resolved' ? resolution : undefined
      );
      
      setShowStatusUpdate(false);
      setResolution('');
      await fetchTicket();
      
      toast.success('وضعیت تیکت به‌روزرسانی شد');
      
    } catch (error) {

      toast.error('خطا در به‌روزرسانی وضعیت');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Get status badge color
  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-blue-100 text-blue-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'waiting_user':
        return 'bg-orange-100 text-orange-800';
      case 'waiting_admin':
        return 'bg-purple-100 text-purple-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get priority badge color
  const getPriorityBadgeColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(dateString));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-4 md:p-6 flex items-center justify-center min-h-96">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">در حال بارگذاری...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!ticket) {
    return (
      <DashboardLayout>
        <div className="p-4 md:p-6 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">تیکت یافت نشد</h1>
          <button
            onClick={() => navigate('/tickets')}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            بازگشت به لیست تیکت‌ها
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4 space-x-reverse">
            <button
              onClick={() => navigate('/tickets')}
              className="text-gray-500 hover:text-gray-700"
            >
              ← بازگشت
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {ticket.title}
              </h1>
              <p className="text-gray-600">
                {ticket.ticketNumber} • شناسه: {ticket.displayId || ticket._id.slice(-8)}
              </p>
            </div>
          </div>
          
          {isAdmin && (
            <button
              onClick={() => setShowStatusUpdate(!showStatusUpdate)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              به‌روزرسانی وضعیت
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Status Update Panel (Admin) */}
            {isAdmin && showStatusUpdate && (
              <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">به‌روزرسانی وضعیت</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      وضعیت جدید
                    </label>
                    <select
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {statusOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {newStatus === 'resolved' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        توضیحات حل مشکل
                      </label>
                      <textarea
                        value={resolution}
                        onChange={(e) => setResolution(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="توضیح دهید که چگونه مشکل حل شد..."
                      />
                    </div>
                  )}

                  <div className="flex space-x-4 space-x-reverse">
                    <button
                      onClick={handleStatusUpdate}
                      disabled={updatingStatus}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                    >
                      {updatingStatus ? 'در حال به‌روزرسانی...' : 'ذخیره'}
                    </button>
                    <button
                      onClick={() => setShowStatusUpdate(false)}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                    >
                      انصراف
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Messages */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">گفتگو</h3>
              </div>
              
              <div className="p-6 max-h-96 overflow-y-auto">
                {/* Initial ticket description */}
                <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-start space-x-3 space-x-reverse">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                        {ticket.userId.name.charAt(0)}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 space-x-reverse mb-1">
                        <span className="text-sm font-medium text-gray-900">
                          {ticket.userId.name} {ticket.userId.lastName}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDate(ticket.createdAt)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">{ticket.description}</p>
                      
                      {/* Initial attachments */}
                      {ticket.attachments.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {ticket.attachments.map((attachment, index) => (
                            <a
                              key={index}
                              href={`${API_CONFIG.baseURL}/files${attachment.fileUrl}?token=${localStorage.getItem('authToken')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center space-x-2 space-x-reverse p-2 bg-white rounded border border-gray-200 hover:bg-gray-50"
                            >
                              <span className="text-lg">{getFileIcon(attachment.mimeType)}</span>
                              <div>
                                <p className="text-xs font-medium text-gray-700">
                                  {attachment.originalName}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {formatFileSize(attachment.fileSize)}
                                </p>
                              </div>
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Messages */}
                {ticket.messages.map((message: TicketMessage) => (
                  <div key={message._id} className="mb-4">
                    <div className={`flex ${message.senderType === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                        message.senderType === 'user' 
                          ? 'bg-blue-500 text-white' 
                          : 'bg-gray-100 text-gray-900'
                      }`}>
                        <div className="flex items-center space-x-2 space-x-reverse mb-1">
                          <span className="text-xs font-medium">
                            {message.sender.name} {message.sender.lastName}
                          </span>
                          <span className={`text-xs ${
                            message.senderType === 'user' ? 'text-blue-100' : 'text-gray-500'
                          }`}>
                            {formatDate(message.timestamp)}
                          </span>
                        </div>
                        <p className="text-sm">{message.message}</p>
                        
                        {/* Message attachments */}
                        {message.attachments.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {message.attachments.map((attachment, index) => (
                              <a
                                key={index}
                                href={`${API_CONFIG.baseURL}/files${attachment.fileUrl}?token=${localStorage.getItem('authToken')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`inline-flex items-center space-x-2 space-x-reverse p-1 rounded text-xs ${
                                  message.senderType === 'user'
                                    ? 'bg-blue-400 hover:bg-blue-600 text-white'
                                    : 'bg-white hover:bg-gray-50 text-gray-700'
                                }`}
                              >
                                <span>{getFileIcon(attachment.mimeType)}</span>
                                <span>{attachment.originalName}</span>
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              {ticket.status !== 'closed' && (
                <form onSubmit={handleSendMessage} className="p-6 border-t border-gray-200">
                  <div className="space-y-4">
                    <textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="پیام خود را بنویسید..."
                      maxLength={1000}
                    />

                    {/* File attachments */}
                    <div>
                      <input
                        type="file"
                        ref={fileInputRef}
                        multiple
                        onChange={handleFileChange}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.txt,.zip,.rar"
                      />

                      {/* Show selected files */}
                      {newAttachments && newAttachments.length > 0 && (
                        <div className="mt-2 space-y-2">
                          {Array.from(newAttachments).map((file, index) => (
                            <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                              <div className="flex items-center space-x-2 space-x-reverse">
                                <span className="text-lg">{getFileIcon(file.type)}</span>
                                <div>
                                  <p className="text-sm font-medium text-gray-700">{file.name}</p>
                                  <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeAttachment(index)}
                                className="text-red-600 hover:text-red-800 text-sm"
                              >
                                حذف
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={sendingMessage || (!newMessage.trim() && (!newAttachments || newAttachments.length === 0))}
                        className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {sendingMessage ? 'در حال ارسال...' : 'ارسال پیام'}
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Ticket Info */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">اطلاعات تیکت</h3>
              
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-gray-600">وضعیت:</span>
                  <span className={`mr-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(ticket.status)}`}>
                    {ticket.statusDisplay}
                  </span>
                </div>
                
                <div>
                  <span className="text-sm font-medium text-gray-600">اولویت:</span>
                  <span className={`mr-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityBadgeColor(ticket.priority)}`}>
                    {ticket.priorityDisplay}
                  </span>
                </div>
                
                <div>
                  <span className="text-sm font-medium text-gray-600">بخش:</span>
                  <span className="mr-2 text-sm text-gray-900">{ticket.departmentDisplay}</span>
                </div>
                
                <div>
                  <span className="text-sm font-medium text-gray-600">تاریخ ایجاد:</span>
                  <span className="mr-2 text-sm text-gray-900">{formatDate(ticket.createdAt)}</span>
                </div>
                
                <div>
                  <span className="text-sm font-medium text-gray-600">آخرین فعالیت:</span>
                  <span className="mr-2 text-sm text-gray-900">{formatDate(ticket.lastActivity)}</span>
                </div>

                {ticket.resolvedAt && (
                  <div>
                    <span className="text-sm font-medium text-gray-600">تاریخ حل:</span>
                    <span className="mr-2 text-sm text-gray-900">{formatDate(ticket.resolvedAt)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* User Info (for admins) */}
            {isAdmin && (
              <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">اطلاعات کاربر</h3>
                
                <div className="space-y-3">
                  <div>
                    <span className="text-sm font-medium text-gray-600">نام:</span>
                    <span className="mr-2 text-sm text-gray-900">
                      {ticket.userId.name} {ticket.userId.lastName}
                    </span>
                  </div>
                  
                  <div>
                    <span className="text-sm font-medium text-gray-600">موبایل:</span>
                    <span className="mr-2 text-sm text-gray-900">{ticket.userId.mobile}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Resolution (if resolved) */}
            {ticket.status === 'resolved' && ticket.resolution && (
              <div className="bg-green-50 rounded-lg border border-green-200 p-6">
                <h3 className="text-lg font-medium text-green-900 mb-2">حل مشکل</h3>
                <p className="text-sm text-green-700">{ticket.resolution}</p>
                {ticket.resolvedBy && (
                  <p className="text-xs text-green-600 mt-2">
                    حل شده توسط: {ticket.resolvedBy.name} {ticket.resolvedBy.lastName}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TicketDetails;